/**
 * Briefing Page
 * API-integrated page for Morning Harvest audio briefings
 */

import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  RefreshCw,
  Calendar,
  Clock,
  Zap,
  History,
  ChevronRight,
  PlayCircle,
  Settings,
} from 'lucide-react';
import { MorningHarvest } from '../components/audio/MorningHarvest';
import {
  useDailyBriefing,
  useGenerateBriefing,
  useSegmentAction,
  useBriefingHistory,
  getSavedProgress,
} from '../services/briefings';
import type { VoiceCommandType } from '../types/audio';

export function BriefingPage() {
  const [showHistory, setShowHistory] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);

  // Fetch today's briefing
  const {
    data: briefing,
    isLoading,
    isError,
    refetch,
  } = useDailyBriefing();

  // Fetch briefing history
  const { data: history = [] } = useBriefingHistory();

  // Generate new briefing mutation
  const generateBriefing = useGenerateBriefing();

  // Process segment action mutation
  const segmentAction = useSegmentAction();

  // Handle segment actions from MorningHarvest
  const handleSegmentAction = useCallback(
    async (segmentId: string, action: VoiceCommandType) => {
      await segmentAction.mutateAsync({
        segmentId,
        action,
        completedAt: new Date(),
      });
    },
    [segmentAction]
  );

  // Handle briefing completion
  const handleComplete = useCallback(() => {
    console.log('Briefing complete!');
  }, []);

  // Handle regenerate briefing
  const handleRegenerate = useCallback(async () => {
    await generateBriefing.mutateAsync({
      voiceId: 'alloy',
      includeTypes: ['summary', 'deadline', 'billing', 'task', 'alert', 'insight'],
      maxDuration: 10,
      summarizationLevel: 'normal',
    });
  }, [generateBriefing]);

  // Check for saved progress
  const savedProgress = briefing ? getSavedProgress(briefing.id) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
          <p className="text-neutral-500">Loading your briefing...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Zap className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Failed to load briefing
          </h3>
          <p className="text-neutral-500 max-w-sm">
            We couldn&apos;t generate your morning briefing. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-24">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                Morning Harvest
              </h1>
              <p className="text-neutral-500 mt-1">
                Your daily audio briefing
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                  ${showHistory
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                    : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                  }
                `}
              >
                <History className="w-4 h-4" />
                History
              </button>

              <button
                onClick={handleRegenerate}
                disabled={generateBriefing.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {generateBriefing.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Regenerate
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Briefing Info Card */}
            <motion.div
              className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                    Today&apos;s Briefing
                  </h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-neutral-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {briefing?.date.toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {briefing ? Math.ceil(briefing.totalDuration / 60) : 0} min
                    </span>
                  </div>
                </div>

                {!autoPlay && (
                  <button
                    onClick={() => setAutoPlay(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <PlayCircle className="w-5 h-5" />
                    Start Briefing
                  </button>
                )}
              </div>

              {/* Segments List */}
              <div className="space-y-3">
                {briefing?.segments.map((segment, index) => (
                  <motion.div
                    key={segment.id}
                    className={`
                      flex items-center gap-4 p-3 rounded-lg border transition-colors
                      ${savedProgress?.completedSegments.includes(segment.id)
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-neutral-50 dark:bg-neutral-700/50 border-neutral-200 dark:border-neutral-600'
                      }
                    `}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-600 flex items-center justify-center text-sm font-medium text-neutral-600 dark:text-neutral-300">
                      {index + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`
                          px-2 py-0.5 rounded text-xs font-medium uppercase
                          ${segment.priority === 'high'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                            : segment.priority === 'low'
                              ? 'bg-neutral-100 dark:bg-neutral-600 text-neutral-500'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                          }
                        `}>
                          {segment.type}
                        </span>
                        <span className="text-xs text-neutral-400">
                          {segment.duration}s
                        </span>
                      </div>
                      <h4 className="font-medium text-neutral-900 dark:text-white mt-1 truncate">
                        {segment.title}
                      </h4>
                    </div>

                    <ChevronRight className="w-5 h-5 text-neutral-400" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Preferences Card */}
            <motion.div
              className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-neutral-200 dark:border-neutral-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-neutral-400" />
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  Preferences
                </h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Voice
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    Alloy
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Max Duration
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    10 min
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Detail Level
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    Normal
                  </span>
                </div>
              </div>
            </motion.div>

            {/* History Panel */}
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-neutral-200 dark:border-neutral-700"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">
                    Past Briefings
                  </h3>

                  <div className="space-y-2">
                    {history.slice(0, 5).map((item) => (
                      <button
                        key={item.id}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                      >
                        <div className="text-left">
                          <p className="text-sm font-medium text-neutral-900 dark:text-white">
                            {item.date.toLocaleDateString()}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {Math.ceil(item.totalDuration / 60)} min
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-neutral-400" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tips Card */}
            <motion.div
              className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-semibold mb-2">Voice Commands</h3>
              <p className="text-sm text-green-100 mb-3">
                Enable the microphone and say:
              </p>
              <ul className="space-y-1 text-sm">
                <li>&quot;Handle that&quot; - Mark as done</li>
                <li>&quot;Skip&quot; - Skip to next</li>
                <li>&quot;Snooze&quot; - Remind later</li>
                <li>&quot;Details&quot; - More info</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Morning Harvest Player */}
      <MorningHarvest
        briefing={briefing ?? null}
        onSegmentAction={handleSegmentAction}
        onComplete={handleComplete}
        autoPlay={autoPlay}
      />
    </div>
  );
}

export default BriefingPage;
